<template>
  <!-- CREATE TRANSACTION DIALOG -->
  <v-dialog
    v-model="isOpen"
    width="600"
    persistent
  >
    <v-card>
      <v-card-title
        class="headline success white--text"
        primary-title
      >
        <v-icon
          dark
          class="mr-2"
        >
          {{ isRevertTransaction ? 'sync_alt' : 'add_circle' }}
        </v-icon>
        {{ isRevertTransaction ? 'Revert' : 'Create' }} Transaction
      </v-card-title>

      <v-form
        ref="createTransactionForm"
        @submit.prevent=""
      >
        <v-progress-linear
          v-if="isCreating"
          indeterminate
          class="pa-0 ma-0"
        />
        <v-card-text>
          <label class="subheading">From</label>
          <v-layout>
            <v-flex
              xs6
              class="pr-1"
            >
              <v-text-field
                v-model="formData.fromUserId"
                label="User ID"
                prepend-inner-icon="person"
                :error-messages="getFieldErrors('fromUserId')"
                :readonly="isRevertTransaction"
                @blur="handleChangeFromUserId"
              />
            </v-flex>
            <v-flex
              xs6
              class="pl-1"
            >
              <v-text-field
                v-if="isRevertTransaction"
                v-model="formData.fromWallet"
                name="fromWallet"
                label="Wallet"
                prepend-inner-icon="account_balance_wallet"
                :error-messages="getFieldErrors('fromWallet')"
                readonly
              />
              <v-autocomplete
                v-else
                v-model="formData.fromWallet"
                name="fromWallet"
                label="Wallet"
                prepend-inner-icon="account_balance_wallet"
                :error-messages="getFieldErrors('fromWallet')"
                :disabled="!formData.fromUserId || fromUserWallets.length == 0"
                :readonly="isRevertTransaction"
                :items="fromUserWallets"
                :loading="isLoadingFromUserWallets"
                @blur="getWalletBalance()"
              />
            </v-flex>
          </v-layout>

          <label class="subheading">To</label>
          <v-layout
            justify-space-between
          >
            <v-flex
              xs6
              class="pr-1"
            >
              <v-text-field
                v-model="formData.toUserId"
                name="toUserId"
                label="User ID"
                prepend-inner-icon="person"
                :error-messages="getFieldErrors('toUserId')"
                :readonly="isRevertTransaction"
                @blur="handleChangeToUserId"
              />
            </v-flex>
            <v-flex
              xs6
              class="pl-1"
            >
              <v-text-field
                v-if="isRevertTransaction"
                v-model="formData.toWallet"
                name="toWallet"
                label="Wallet"
                prepend-inner-icon="account_balance_wallet"
                readonly
              />
              <v-autocomplete
                v-else
                v-model="formData.toWallet"
                name="toWallet"
                label="Wallet"
                prepend-inner-icon="account_balance_wallet"
                :readonly="isRevertTransaction"
                :disabled="!formData.toUserId || toUserWallets.length == 0"
                :items="toUserWallets"
                :loading="isLoadingToUserWallets"
                :error-messages="getFieldErrors('toWallet')"
              />
            </v-flex>
          </v-layout>
          <label class="subheading">Transaction details</label>
          <v-layout>
            <v-flex
              xs6
              class="pr-1"
            >
              <v-autocomplete
                v-model="formData.token"
                :readonly="isRevertTransaction"
                :error-messages="getFieldErrors('token')"
                :items="getTokensWithBalance()"
                name="token"
                label="Token"
                :loading="isLoadingTokenBalance"
                @blur="handleChangeToken()"
              />
            </v-flex>
            <v-flex
              xs6
              class="pl-1"
            >
              <v-text-field
                v-model="formData.amount"
                :error-messages="getFieldErrors('amount')"
                :readonly="isRevertTransaction"
                label="Amount"
                type="number"
                :disabled="!formData.fromWallet || !formData.token || isLoadingTokenBalance"
                :loading="isLoadingTokenBalance"
                @blur="handleChangeAmount()"
              />
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex
              xs6
              class="pr-1"
            >
              <v-select
                v-if="!isRevertTransaction"
                v-model="formData.mask"
                :items="externalIdentifierMasks"
                item-text="name"
                item-value="name"
                label="Type"
              />
            </v-flex>
            <v-flex
              xs6
              class="pl-1"
            >
              <v-text-field
                v-if="!isRevertTransaction && formData.mask == 'manual-sfcase'"
                v-model="formData.sfCaseId"
                :error-messages="getFieldErrors('sfCaseId')"
                label="Sales Force Case ID"
                @blur="$v.formData.sfCaseId.$touch()"
              />
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex xs12>
              <v-text-field
                v-model="formData.extIdentifier"
                :readonly="isRevertTransaction || formData.mask !== 'other'"
                :hint="getHint()"
                persistent-hint
                :error-messages="getFieldErrors('extIdentifier')"
                label="External Identifier"
                @blur="$v.formData.extIdentifier.$touch()"
              />
            </v-flex>
          </v-layout>
          <v-flex
            v-if="vaultErrorOntransactionCreated"
            xs12
            class="error--text"
          >
            {{ vaultErrorOntransactionCreated }}
          </v-flex>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-flex>
            <v-checkbox
              v-if="!isRevertTransaction"
              v-model="keepTransferDialogOpen"
              class="mt-0"
              color="primary"
              label="Keep open after create"
              hide-details
            />
          </v-flex>
          <v-spacer />
          <v-btn
            flat
            @click="onCloseDialog()"
          >
            Close
          </v-btn>
          <v-btn
            :loading="isCreating"
            :disabled="isLoadingFromUserWallets || isLoadingToUserWallets || isCreating || $v.$invalid"
            flat
            @click="confirmDialogOpen = true"
          >
            {{ isRevertTransaction ? 'Revert' : 'Create' }} transaction
          </v-btn>
        </v-card-actions>
      </v-form>

      <ConfirmDialog
        :dialog="confirmDialogOpen"
        :confirm-btn-label="'Confirm'"
        :cancel-btn-label="'Cancel'"
        :head-text="'Create new transaction'"
        :description="'Are you sure to create a new transaction?'"
        :confirm-action="_confirmCreate"
        :cancel-action="_cancelCreate"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash';

import { mapActions, mapGetters } from 'vuex';
import { required, between } from 'vuelidate/lib/validators';
import ConfirmDialog from '../../shared/ConfirmDialog';

export default {
  name: 'CreateVaultTransactionDialog',
  components: {
    ConfirmDialog,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    revertTransaction: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      confirmDialogOpen: false,
      keepTransferDialogOpen: false,
      formData: {
        fromUserId: null,
        fromWallet: null,
        toUserId: null,
        toWallet: null,
        amount: null,
        token: null,
        mask: null,
        sfCaseId: null,
        extIdentifier: null,
      },
      fromUserWallets: [],
      toUserWallets: [],
      isLoadingFromUserWallets: false,
      isLoadingToUserWallets: false,
      isLoadingTokenBalance: false,
      isCreating: false,
      externalIdentifierMasks: [
        { name: 'account-merge', hint: 'account-merge-{wallet_name_from}-{wallet_name_to}-{token_symbol}' },
        { name: 'dda-merge', hint: 'dda-merge-{wallet_name_from}-{wallet_name_to}-{token_symbol}' },
        { name: 'inheritance', hint: 'inheritance-{wallet_name_from}-{wallet_name_to}-{token_symbol}' },
        { name: 'manual-sfcase', hint: 'manual-sfcase-{sf_case_id}-{wallet_name_from}-{wallet_name_to}-{token_symbol}' },
        { name: 'other', hint: '' },
      ],
      tokenBalance: 0,
      balances: [],
    };
  },
  computed: {
    ...mapGetters([
      'vaultAssetsTokens',
      'vaultErrorOntransactionCreated',
    ]),
    isRevertTransaction() {
      return !!this.revertTransaction;
    },
  },
  watch: {
    revertTransaction: {
      async handler(transaction) {
        if (transaction) {
          this.formData = {
            fromUserId: transaction.toUserId,
            fromWallet: transaction.toWallet,
            toUserId: transaction.fromUserId,
            toWallet: transaction.wallet,
            amount: transaction.amount,
            token: transaction.token,
            extIdentifier: `revert-${transaction.extIdentifier}`,
          };
          await this.getWalletBalance();
          await this.getTokensWithBalance();
          this.handleChangeToken();
        }
      },
    },
    'formData.mask': function (newMask) {
      if (newMask === 'other') {
        this.formData.extIdentifier = null;
      }
    },
    'formData.extIdentifier': function () {
      this.$v.formData.extIdentifier.$touch();
    },
    formData: {
      deep: true,
      handler() {
        if (this.isRevertTransaction || this.formData.mask === 'other') {
          return;
        }

        // set field data automatically based on selected mask
        this.updateFormExtIdentifier();
      },
    },
  },
  mounted() {

  },
  validations() {
    return {
      formData: {
        fromUserId: { required },
        fromWallet: { required },
        toUserId: { required },
        toWallet: { required },
        amount: { required, between: between(1, this.tokenBalance) },
        token: { required },
        extIdentifier: { required },
        ...(this.formData.mask && this.formData.mask === 'manual-sfcase') && { sfCaseId: { required } },
      },
    };
  },
  methods: {
    async handleChangeFromUserId() {
      this.$v.formData.fromUserId.$touch();
      if (this.formData.fromUserId) {
        this.isLoadingFromUserWallets = true;
        this.fromUserWallets = await this.loadWallets(this.formData.fromUserId);
        this.isLoadingFromUserWallets = false;
      }
    },
    async handleChangeToUserId() {
      this.$v.formData.toUserId.$touch();
      if (this.formData.toUserId) {
        this.isLoadingToUserWallets = true;
        this.toUserWallets = await this.loadWallets(this.formData.toUserId);
        this.isLoadingToUserWallets = false;
      }
    },
    async handleChangeAmount() {
      this.$v.formData.amount.$touch();
    },
    getTokensWithBalance() {
      return this.vaultAssetsTokens.map(
        token => ({ text: `${token.name} - ${token.token}`, value: token.token }),
      ).filter(token => this.balances.find(balanceToken => balanceToken.token === token.value));
    },
    handleChangeToken() {
      const balance = this.balances.find(asset => asset.token === this.formData.token);

      if (balance) {
        this.tokenBalance = balance.balance;
      }

      if (this.formData.amount && this.formData.fromWallet && this.formData.token) {
        this.$v.formData.amount.$touch();
      }
    },
    async loadWallets(id) {
      const wallets = (await this.getUserWallets(id)).data;

      return wallets.map(wallet => wallet.wallet);
    },
    async getWalletBalance() {
      this.isLoadingTokenBalance = true;
      this.balances = await this.getVaultWalletBalances({ userId: this.formData.fromUserId, wallet: this.formData.fromWallet });

      this.isLoadingTokenBalance = false;

      return this.balances;
    },
    async handleSubmitCreateTransaction() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isCreating = true;
        await this.createVaultTransaction(this.formData);
        this.isCreating = false;

        if (!this.keepTransferDialogOpen) {
          this.onCloseDialog();
        } else {
          this.$v.$reset();
          delete this.formData.amount;
          delete this.formData.token;
        }
        this.$forceUpdate();
        this.onCreateTransaction();
      }
    },
    updateFormExtIdentifier() {
      this.formData.extIdentifier = this.getHint()
        .replace('{sf_case_id}', this.formData.sfCaseId || '')
        .replace('{wallet_name_from}', this.formData.fromWallet || '')
        .replace('{wallet_name_to}', this.formData.toWallet || '')
        .replace('{token_symbol}', this.getTokenSymbol(this.formData.token) || '');
    },
    clearFormData() {
      this.$v.$reset();
      this.formData = {
        fromUserId: null,
        fromWallet: null,
        toUserId: null,
        toWallet: null,
        amount: null,
        token: null,
        mask: null,
        sfCaseId: null,
        extIdentifier: null,
      };
    },
    getFieldErrors(field) {
      const errors = [];
      if (this.$v.formData[field]) {
        if (!this.$v.formData[field].$dirty || !this.$v.formData[field].$invalid) {
          return errors;
        }

        if (!this.$v.formData[field].required) {
          errors.push('Required field');
        }

        if (this.$v.formData[field].between === false) {
          errors.push(`Amount must be between ${this.$v.formData[field].$params.between.min} and ${this.$v.formData[field].$params.between.max}`);
        }
      }

      return errors;
    },
    getTokenSymbol(token) {
      const asset = this.vaultAssetsTokens.find(item => item.token === token);

      return asset ? asset.name : token;
    },
    getHint() {
      const mask = this.externalIdentifierMasks.find(m => m.name === this.formData.mask);

      return mask ? mask.hint : '';
    },
    ...mapActions([
      'getUserWallets',
      'createVaultTransaction',
      'getVaultWalletBalances',
    ]),
    _cancelCreate() {
      this.confirmDialogOpen = false;
    },
    _confirmCreate() {
      this.handleSubmitCreateTransaction();
      this.confirmDialogOpen = false;
    },
    onCloseDialog() {
      this.$emit('onCloseDialog');
      this.clearFormData();
    },
    onCreateTransaction() {
      this.$emit('onCreateTransaction');
    },
  },
};
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/components/vault/dialog/CreateVaultTransactionDialog.vue